(() => {
  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      console.warn('[RF enhancements] Could not parse JSON payload', error);
      return fallback;
    }
  };

  class RFCartVariantEditor extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const externalTriggerId = this.dataset.externalTriggerId || '';
      this.trigger = externalTriggerId ? document.getElementById(externalTriggerId) : this.querySelector('[data-rf-cart-edit-trigger]');
      this.panel = this.querySelector('[data-rf-cart-edit-panel]');
      this.hostEditorRow = this.closest('.rf-main-cart-editor-row');
      this.fields = this.querySelector('[data-rf-cart-edit-fields]');
      this.status = this.querySelector('[data-rf-cart-edit-status]');
      this.saveButton = this.querySelector('[data-rf-cart-edit-save]');
      this.cancelButtons = Array.from(this.querySelectorAll('[data-rf-cart-edit-cancel]'));
      this.priceCurrent = this.querySelector('[data-rf-cart-edit-current-price]');
      this.priceCompare = this.querySelector('[data-rf-cart-edit-compare-price]');
      this.qtyInput = this.querySelector('[data-rf-cart-edit-qty-input]');
      this.qtyMinus = this.querySelector('[data-rf-cart-edit-qty-minus]');
      this.qtyPlus = this.querySelector('[data-rf-cart-edit-qty-plus]');
      this.variants = parseJson(this.querySelector('[data-rf-cart-variants]'), []);
      this.optionNames = parseJson(this.querySelector('[data-rf-cart-option-names]'), []);
      this.currentOptions = parseJson(this.querySelector('[data-rf-cart-current-options]'), []);
      this.properties = parseJson(this.querySelector('[data-rf-cart-properties]'), {});
      this.line = Number(this.dataset.line || 0);
      this.lineKey = this.dataset.lineKey || '';
      this.quantity = Number(this.dataset.quantity || 1);
      this.desiredQuantity = this.quantity;
      this.currentVariantId = Number(this.dataset.currentVariantId || 0);
      this.currentVariantPrice = Number(this.dataset.currentVariantPrice || 0);
      this.currentFinalPrice = Number(this.dataset.currentFinalPrice || 0);
      this.sellingPlanId = this.dataset.sellingPlanId || '';
      this.optionButtons = [];
      this.loading = false;

      this.buildFields();
      this.buildShippingProtection();
      this.trigger?.addEventListener('click', this.handleToggle);
      this.cancelButtons.forEach((button) => button.addEventListener('click', this.close));
      this.saveButton?.addEventListener('click', this.updateVariant);
      this.qtyMinus?.addEventListener('click', () => this.adjustQuantity(-1));
      this.qtyPlus?.addEventListener('click', () => this.adjustQuantity(1));
      this.qtyInput?.addEventListener('input', this.handleQuantityInput);
      this.qtyInput?.addEventListener('change', this.handleQuantityCommit);
      document.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    buildFields() {
      if (!this.fields || !Array.isArray(this.variants) || !this.variants.length) return;
      this.fields.innerHTML = '';
      this.optionButtons = [];

      const optionCount = Math.max(
        this.optionNames.length,
        this.currentOptions.length,
        ...this.variants.map((variant) => Array.isArray(variant.options) ? variant.options.length : 0)
      );

      for (let index = 0; index < optionCount; index += 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'rf-cart-variant-editor__field';

        const label = document.createElement('label');
        label.textContent = this.optionNames[index] || `Option ${index + 1}`;

        const choices = document.createElement('div');
        choices.className = 'rf-cart-variant-editor__choices';

        const values = [];
        this.variants.forEach((variant) => {
          const value = variant.options?.[index];
          if (value != null && !values.includes(value)) values.push(value);
        });

        const buttons = [];
        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'rf-cart-variant-editor__choice';
          button.dataset.optionIndex = String(index);
          button.dataset.value = value;
          button.textContent = value;
          if (value === this.currentOptions[index]) button.classList.add('is-active');
          button.addEventListener('click', () => {
            if (this.loading || button.disabled) return;
            this.currentOptions[index] = value;
            this.updateButtonState();
            this.handleSelectionChange();
          });
          choices.appendChild(button);
          buttons.push(button);
        });

        wrapper.append(label, choices);
        this.fields.appendChild(wrapper);
        this.optionButtons.push(buttons);
      }

      this.updateButtonState();
      this.syncQuantityControls();
      this.handleSelectionChange();
    }

    // Store configuration: the ACO Shipping Protection field from the product
    // form supplied for Northryn Active. Keep this in one place for maintenance.
    shippingConfig() {
      return {
        id: 'checkbox_9311494301',
        field: { label: 'Shipping Protection', value: 'Yes [$1.99]', _value: true },
      };
    }

    readACO(value, fallback = null) {
      try { return typeof value === 'string' ? JSON.parse(value) : (value || fallback); }
      catch (_) { return fallback; }
    }

    shippingState(properties = {}) {
      const hidden = this.readACO(properties?._acpaHidden, {}) || {};
      const entry = Object.entries(hidden).find(([, field]) => String(field?.label || '').trim().toLowerCase() === 'shipping protection');
      const saved = this.readACO(properties?._rf_shipping_protection);
      const template = entry ? { id: entry[0], field: entry[1] } : (saved || this.shippingConfig());
      const value = String(template.field?.value || '');
      const match = value.match(/\[\$([0-9]+(?:\.[0-9]+)?)\]/);
      const amount = match ? Number(match[1]) : 1.99;
      const property = String(properties?.['Shipping Protection'] || '').trim();
      const checked = entry
        ? entry[1]._value !== false && entry[1]._value !== 'false' && Boolean(property)
        : Boolean(property && !/^(no|false|0)$/i.test(property));
      return { ...template, amount, checked };
    }

    buildShippingProtection() {
      this.shipping = this.shippingState(this.properties);
      if (!this.shipping || !this.fields) return;
      const label = document.createElement('label');
      label.className = 'rf-cart-variant-editor__shipping';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = this.shipping.checked;
      input.addEventListener('change', this.handleSelectionChange);
      const text = document.createElement('span');
      text.textContent = `Shipping Protection (${this.formatMoney(Math.round(this.shipping.amount * 100))})`;
      label.append(input, text);
      this.fields.appendChild(label);
      this.shippingInput = input;
      this.handleSelectionChange();
    }

    shippingChanged() {
      return Boolean(this.shippingInput && this.shippingInput.checked !== this.shipping.checked);
    }

    updatedACOProperties(properties, variant, quantity) {
      const result = { ...properties };
      const shipping = this.shippingState(properties);
      let price = this.readACO(result._acoPrice);
      if (!price && !this.shippingInput) return result;
      if (!price) {
        if (result._acoPrice) throw new Error('The add-on price data is invalid. Please refresh your cart.');
        price = {
          fee: 0, option: shipping.checked ? shipping.amount : 0,
          product: Number(variant.price) / 100, total: 0,
          feeBreak: {}, fee_elements: {}, quantity, exchange_rate: 1,
        };
      }
      if (this.shippingInput && !shipping) throw new Error('Shipping Protection details changed. Please refresh your cart.');
      if (shipping && this.shippingInput) {
        const checked = this.shippingInput.checked;
        const hidden = this.readACO(result._acpaHidden, {});
        result._rf_shipping_protection = JSON.stringify({ id: shipping.id, field: shipping.field });
        if (checked) {
          hidden[shipping.id] = { ...shipping.field, _value: true };
          result['Shipping Protection'] = shipping.field.value;
        } else {
          delete hidden[shipping.id];
          delete result['Shipping Protection'];
        }
        result._acpaHidden = JSON.stringify(hidden);
        const delta = (Number(checked) - Number(shipping.checked)) * shipping.amount;
        price.option = Math.round((Number(price.option) + delta) * 100) / 100;
        if (price.option < 0) throw new Error('Shipping Protection price could not be verified. Please refresh your cart.');
      }
      price.product = Number(variant.price) / 100;
      price.quantity = quantity;
      price.total = Math.round((price.product + Number(price.option || 0) + Number(price.fee || 0)) * 100) / 100;
      result._acoPrice = JSON.stringify(price);
      return result;
    }

    handleToggle = () => {
      if (!this.panel) return;
      this.panel.hidden ? this.open() : this.close();
    };

    open = () => {
      if (!this.panel) return;
      if (this.hostEditorRow) this.hostEditorRow.hidden = false;
      this.panel.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
    };

    close = () => {
      if (!this.panel || this.loading) return;
      this.panel.hidden = true;
      if (this.hostEditorRow) this.hostEditorRow.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.clearStatus();
      this.trigger?.focus({ preventScroll: true });
    };

    handleKeydown = (event) => {
      if (!this.panel || this.panel.hidden || event.key !== 'Escape') return;
      // Inline editors only handle Escape while focus is inside this editor.
      if (!this.panel.contains(event.target) && event.target !== this.trigger) return;
      event.preventDefault();
      this.close();
    };

    normalizedQuantity(value = this.qtyInput?.value) {
      const parsed = Math.floor(Number(value || 1));
      if (!Number.isFinite(parsed)) return 1;
      return Math.max(1, Math.min(99, parsed));
    }

    syncQuantityControls() {
      this.desiredQuantity = this.normalizedQuantity(this.desiredQuantity);
      if (this.qtyInput) this.qtyInput.value = String(this.desiredQuantity);
      if (this.qtyMinus) this.qtyMinus.disabled = this.loading || this.desiredQuantity <= 1;
      if (this.qtyPlus) this.qtyPlus.disabled = this.loading || this.desiredQuantity >= 99;
    }

    adjustQuantity(delta) {
      if (this.loading) return;
      this.desiredQuantity = this.normalizedQuantity(this.desiredQuantity + delta);
      this.syncQuantityControls();
      this.handleSelectionChange();
    }

    handleQuantityInput = () => {
      if (this.loading) return;
      const parsed = Number(this.qtyInput?.value || '');
      if (Number.isFinite(parsed) && parsed >= 1) this.desiredQuantity = Math.min(99, Math.floor(parsed));
      this.handleSelectionChange();
    };

    handleQuantityCommit = () => {
      if (this.loading) return;
      this.desiredQuantity = this.normalizedQuantity();
      this.syncQuantityControls();
      this.handleSelectionChange();
    };

    selectedOptions() {
      return this.optionButtons.map((group, index) => {
        const active = group.find((button) => button.classList.contains('is-active'));
        return active?.dataset.value || this.currentOptions[index] || '';
      });
    }

    updateButtonState() {
      this.optionButtons.forEach((group, index) => {
        group.forEach((button) => {
          const isActive = button.dataset.value === this.currentOptions[index];
          button.classList.toggle('is-active', isActive);
        });
      });
    }

    variantMatchesSelection(variant, selection, ignoreIndex = -1) {
      if (!Array.isArray(variant?.options)) return false;
      return selection.every((value, optionIndex) => {
        if (optionIndex === ignoreIndex) return true;
        if (!value) return true;
        return variant.options[optionIndex] === value;
      });
    }

    refreshChoiceAvailability() {
      const selection = this.selectedOptions();
      this.optionButtons.forEach((group, optionIndex) => {
        group.forEach((button) => {
          const candidateSelection = [...selection];
          candidateSelection[optionIndex] = button.dataset.value || '';
          const exists = this.variants.some((variant) => this.variantMatchesSelection(variant, candidateSelection));
          const available = this.variants.some((variant) => variant.available && this.variantMatchesSelection(variant, candidateSelection));
          const disabled = !exists;
          button.disabled = disabled || this.loading;
          button.classList.toggle('is-unavailable', exists && !available);
        });
      });
    }

    selectedVariant() {
      const selectedOptions = this.selectedOptions();
      return this.variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        return selectedOptions.every((value, index) => variant.options[index] === value);
      });
    }

    variantPrice(variant) {
      const raw = variant?.price;
      if (raw == null || raw === '') return 0;
      return Number(raw);
    }

    formatMoney(cents) {
      try {
        if (window.FoxTheme?.Currency?.formatMoney && window.FoxTheme?.settings?.moneyFormat) {
          return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings.moneyFormat);
        }
      } catch (error) {
        console.warn('[RF enhancements] Could not format money with FoxTheme formatter', error);
      }
      const amount = Number(cents || 0) / 100;
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amount);
    }

    updateDisplayedPrice(variant) {
      if (!this.priceCurrent) return;
      let currentPrice = Number(variant?.price ?? this.currentFinalPrice ?? 0);
      // Show the same ACO add-on amount that will be submitted on Update.
      if (variant && (this.properties?._acoPrice || this.shippingInput)) {
        try {
          const updated = this.updatedACOProperties(this.properties, variant, this.desiredQuantity);
          const price = this.readACO(updated._acoPrice);
          if (price && Number.isFinite(Number(price.total))) currentPrice = Math.round(Number(price.total) * 100);
        } catch (_) { /* Saving will report invalid app metadata. */ }
      }
      const comparePrice = Number(variant?.compare_at_price ?? 0);
      this.priceCurrent.textContent = this.formatMoney(currentPrice);
      if (this.priceCompare) {
        if (comparePrice > currentPrice) {
          this.priceCompare.textContent = this.formatMoney(comparePrice);
          this.priceCompare.hidden = false;
        } else {
          this.priceCompare.hidden = true;
        }
      }
    }

    handleSelectionChange = () => {
      this.refreshChoiceAvailability();
      const variant = this.selectedVariant();
      this.updateDisplayedPrice(variant || null);

      if (!variant) {
        this.setStatus('This option combination is unavailable.', true);
        if (this.saveButton) this.saveButton.disabled = true;
        return;
      }

      if (!variant.available) {
        this.setStatus('This option combination is sold out.', true);
        if (this.saveButton) this.saveButton.disabled = true;
        return;
      }

      const isCurrentVariant = Number(variant.id) === this.currentVariantId;
      const quantityChanged = this.normalizedQuantity(this.desiredQuantity) !== this.quantity;
      const hasChanges = !isCurrentVariant || quantityChanged || this.shippingChanged();
      if (this.saveButton) this.saveButton.disabled = this.loading || !hasChanges;
      this.syncQuantityControls();

      if (!hasChanges) {
        this.setStatus('You are already using these options.');
      } else if (isCurrentVariant && quantityChanged) {
        this.setStatus('Ready to update quantity.');
      } else {
        this.setStatus('Ready to update. Personalization will stay the same.');
      }
    };

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', Boolean(isError));
    }

    clearStatus() {
      this.setStatus('');
    }

    setLoading(loading) {
      this.loading = loading;
      if (this.shippingInput) this.shippingInput.disabled = loading;
      if (this.saveButton) this.saveButton.classList.toggle('is-loading', loading);
      this.refreshChoiceAvailability();
      this.syncQuantityControls();
      if (!loading) this.handleSelectionChange();
      if (this.cancelButtons?.length) this.cancelButtons.forEach((button) => { button.disabled = loading; });
    }

    ajaxRoute(path) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}${path}`.replace(/\/+/g, '/');
    }

    async parseCartResponse(response) {
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = { raw: text };
      }

      if (!response.ok || data.status) {
        let detail = data.description || data.message || data.errors;
        if (detail && typeof detail === 'object') {
          detail = Object.values(detail).flat().filter(Boolean).join(' ');
        }
        if (!detail && data.raw && data.raw.length < 260) detail = data.raw;
        const requestError = new Error(detail || `Shopify cart request failed (${response.status}).`);
        requestError.status = response.status;
        requestError.payload = data;
        throw requestError;
      }
      return data;
    }

    async getCart() {
      const response = await fetch(this.ajaxRoute('cart.js'), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        cache: 'no-store',
      });
      return this.parseCartResponse(response);
    }

    findLiveLine(cart) {
      if (!cart?.items?.length) return null;
      if (this.lineKey) {
        const keyed = cart.items.find((item) => item.key === this.lineKey);
        if (keyed) return keyed;
      }
      const indexed = this.line > 0 ? cart.items[this.line - 1] : null;
      if (indexed && Number(indexed.variant_id) === this.currentVariantId) return indexed;
      return cart.items.find((item) => Number(item.variant_id) === this.currentVariantId) || null;
    }

    createEditToken() {
      if (window.crypto?.randomUUID) return `rf-${window.crypto.randomUUID()}`;
      return `rf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    normalizeProperties(properties, token = '') {
      const normalized = {};
      Object.entries(properties || {}).forEach(([key, value]) => {
        if (!key || value == null) return;
        normalized[key] = value;
      });
      if (token) normalized._rf_variant_edit_uid = token;
      return normalized;
    }

    buildAddForm(variantId, snapshot, token = '') {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(snapshot.quantity));

      const properties = this.normalizeProperties(snapshot.properties, token);
      Object.entries(properties).forEach(([key, value]) => {
        let normalized = value;
        if (typeof normalized === 'object') normalized = JSON.stringify(normalized);
        formData.append(`properties[${key}]`, String(normalized));
      });

      if (snapshot.sellingPlanId) formData.append('selling_plan', String(snapshot.sellingPlanId));
      return formData;
    }

    async addVariantToCart(variantId, snapshot, token = '') {
      const response = await fetch(this.ajaxRoute('cart/add.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: this.buildAddForm(variantId, snapshot, token),
      });
      return this.parseCartResponse(response);
    }

    async changeLineQuantity(id, quantity) {
      const response = await fetch(this.ajaxRoute('cart/change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id, quantity }),
      });
      return this.parseCartResponse(response);
    }

    async removeLine(lineKey, lineNumber = 0) {
      if (lineKey) return this.changeLineQuantity(lineKey, 0);
      const response = await fetch(this.ajaxRoute('cart/change.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ line: lineNumber || this.line, quantity: 0 }),
      });
      return this.parseCartResponse(response);
    }

    async refreshCart(openDrawer = true) {
      if (document.body.classList.contains('cart-template')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: openDrawer },
        })
      );
    }

    makeSnapshot(cart, liveLine, desiredQuantity = null) {
      const originalQuantity = Number(liveLine.quantity || this.quantity || 1);
      return {
        key: liveLine.key || this.lineKey,
        variantId: Number(liveLine.variant_id || this.currentVariantId),
        originalQuantity,
        quantity: desiredQuantity == null ? originalQuantity : this.normalizedQuantity(desiredQuantity),
        properties: { ...(liveLine.properties || this.properties || {}) },
        sellingPlanId: liveLine.selling_plan_allocation?.selling_plan?.id || this.sellingPlanId || '',
        originalPrice: Number(liveLine.original_price ?? liveLine.price ?? this.currentVariantPrice ?? 0),
        finalPrice: Number(liveLine.final_price ?? this.currentFinalPrice ?? liveLine.price ?? 0),
        itemCount: Number(cart.item_count || 0),
      };
    }

    findTokenLine(cart, token) {
      return cart?.items?.find((item) => item.properties?._rf_variant_edit_uid === token) || null;
    }

    assertPreserved(snapshot, cart, newLine) {
      if (!newLine) throw new Error('The updated cart item could not be verified.');

      for (const [key, value] of Object.entries(snapshot.properties || {})) {
        if (key === '_rf_variant_edit_uid') continue;
        const expected = typeof value === 'object' ? JSON.stringify(value) : String(value);
        if (String(newLine.properties?.[key] ?? '') !== expected) throw new Error('Cart item properties could not be verified.');
      }
      if (!Object.hasOwn(snapshot.properties || {}, 'Shipping Protection') && newLine.properties?.['Shipping Protection']) throw new Error('Shipping Protection was not removed.');

      const expectedItemCount = snapshot.itemCount - snapshot.originalQuantity + snapshot.quantity;
      if (Number(newLine.quantity) !== snapshot.quantity || Number(cart.item_count) !== expectedItemCount) {
        const error = new Error('Quantity changed unexpectedly during the update. The original item will be restored.');
        error.code = 'RF_QUANTITY_CHANGED';
        throw error;
      }
    }

    async restoreOriginal(snapshot, newLineKey = '') {
      if (newLineKey) {
        try {
          await this.removeLine(newLineKey);
        } catch (removeError) {
          console.error('[RF cart variant editor] Could not remove failed replacement', removeError);
        }
      }

      const rollbackToken = snapshot.properties?._rf_variant_edit_uid || this.createEditToken();
      const rollbackSnapshot = { ...snapshot, quantity: snapshot.originalQuantity };
      await this.addVariantToCart(snapshot.variantId, rollbackSnapshot, rollbackToken);
    }

    updateVariant = async () => {
      if (this.loading) return;
      const variant = this.selectedVariant();
      const desiredQuantity = this.normalizedQuantity(this.desiredQuantity);

      if (!variant || !variant.available) {
        this.setStatus('Please choose an available option combination.', true);
        return;
      }

      if (!window.Shopify?.routes?.root) {
        this.setStatus('Cart update is temporarily unavailable. Please refresh and try again.', true);
        return;
      }

      this.setLoading(true);
      this.setStatus('Updating your cart item…');

      let snapshot = null;
      let replacementLineKey = '';
      let oldLineRemoved = false;

      try {
        const cartBefore = await this.getCart();
        const liveLine = this.findLiveLine(cartBefore);
        if (!liveLine) throw new Error('This cart item could not be found. Please refresh and try again.');

        const liveQuantity = Number(liveLine.quantity || this.quantity || 1);
        const variantChanged = Number(variant.id) !== Number(liveLine.variant_id || this.currentVariantId);
        const quantityChanged = desiredQuantity !== liveQuantity;

        const shippingChanged = this.shippingChanged();
        if (!variantChanged && !quantityChanged && !shippingChanged) {
          this.setLoading(false);
          this.close();
          return;
        }

        if (!variantChanged && !shippingChanged && !liveLine.properties?._acoPrice) {
          await this.changeLineQuantity(liveLine.key || this.lineKey || this.line, desiredQuantity);
          this.setStatus('Quantity updated.');
          await this.refreshCart(true);
          return;
        }

        snapshot = this.makeSnapshot(cartBefore, liveLine, desiredQuantity);
        const replacementSnapshot = { ...snapshot, properties: this.updatedACOProperties(snapshot.properties, variant, desiredQuantity) };
        await this.removeLine(snapshot.key, this.line);
        oldLineRemoved = true;

        const editToken = this.createEditToken();
        await this.addVariantToCart(Number(variant.id), replacementSnapshot, editToken);

        const cartAfter = await this.getCart();
        const replacementLine = this.findTokenLine(cartAfter, editToken);
        replacementLineKey = replacementLine?.key || '';
        this.assertPreserved(replacementSnapshot, cartAfter, replacementLine);

        this.setStatus('Options and quantity updated. Personalization was kept.');
        await this.refreshCart(true);
      } catch (error) {
        console.error('[RF cart variant editor]', error);

        if (oldLineRemoved && snapshot) {
          try {
            await this.restoreOriginal(snapshot, replacementLineKey);
            this.setStatus(`${error.message || 'The item could not be updated.'} Your original item was restored.`, true);
            await this.refreshCart(true);
          } catch (rollbackError) {
            console.error('[RF cart variant editor rollback]', rollbackError);
            this.setStatus('The item could not be updated and the cart needs a refresh. Please refresh the page before continuing.', true);
          }
        } else {
          this.setStatus(error.message || 'We could not update this item. Please try again.', true);
        }

        this.setLoading(false);
      }
    };
  }

  if (!customElements.get('rf-cart-variant-editor')) {
    customElements.define('rf-cart-variant-editor', RFCartVariantEditor);
  }

  class RFSupportWidget extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.toggleButton = this.querySelector('[data-rf-support-toggle]');
      this.closeButton = this.querySelector('[data-rf-support-close]');
      this.panel = this.querySelector('[data-rf-support-panel]');
      this.success = this.querySelector('[data-rf-support-success]');
      this.form = this.querySelector('form');
      this.sendButton = this.querySelector('.rf-support-widget__send');

      this.toggleButton?.addEventListener('click', this.toggle);
      this.closeButton?.addEventListener('click', this.close);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.panel?.addEventListener('pointerdown', this.stopPanelPropagation);
      this.panel?.addEventListener('click', this.stopPanelPropagation);
      this.panel?.addEventListener('touchstart', this.stopPanelPropagation, { passive: true });
      this.form?.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      this.syncVisualViewport();
      window.visualViewport?.addEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.addEventListener('scroll', this.syncVisualViewport);

      if (this.success || window.location.hash === '#RFSupportContactForm') {
        this.open();
        requestAnimationFrame(() => this.success?.focus());
      }
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.toggle);
      this.closeButton?.removeEventListener('click', this.close);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.panel?.removeEventListener('pointerdown', this.stopPanelPropagation);
      this.panel?.removeEventListener('click', this.stopPanelPropagation);
      this.panel?.removeEventListener('touchstart', this.stopPanelPropagation);
      this.form?.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      window.visualViewport?.removeEventListener('resize', this.syncVisualViewport);
      window.visualViewport?.removeEventListener('scroll', this.syncVisualViewport);
      document.documentElement.classList.remove('rf-support-open');
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    stopPanelPropagation = (event) => {
      // Do not preventDefault: form controls must retain native tap/focus behavior.
      event.stopPropagation();
    };

    syncVisualViewport = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const offsetTop = viewport?.offsetTop || 0;
      this.style.setProperty('--rf-visual-viewport-height', `${Math.max(280, height)}px`);
      this.style.setProperty('--rf-visual-viewport-top', `${Math.max(0, offsetTop)}px`);
    };

    onFocusIn = (event) => {
      const field = event.target.closest?.('input, textarea, select');
      if (!field || !this.contains(field)) return;
      this.classList.add('is-input-focused');
      this.syncVisualViewport();

      // Recalculate after the software keyboard has resized the visual viewport.
      window.setTimeout(this.syncVisualViewport, 80);
      window.setTimeout(this.syncVisualViewport, 280);
    };

    onFocusOut = () => {
      window.setTimeout(() => {
        const active = document.activeElement;
        const stillEditing = active && this.contains(active) && active.matches?.('input, textarea, select');
        if (!stillEditing) this.classList.remove('is-input-focused');
        this.syncVisualViewport();
      }, 120);
    };

    onSubmit = () => {
      if (!this.form?.checkValidity()) return;
      if (this.sendButton) {
        this.sendButton.disabled = true;
        this.sendButton.setAttribute('aria-busy', 'true');
        this.sendButton.textContent = 'Sending…';
      }
    };

    toggle = (event) => {
      event?.stopPropagation();
      if (!this.panel) return;
      this.panel.hidden ? this.open() : this.close();
    };

    open = () => {
      if (!this.panel) return;
      this.syncVisualViewport();
      this.panel.hidden = false;
      this.panel.classList.remove('is-opening');
      void this.panel.offsetWidth;
      this.panel.classList.add('is-opening');
      this.toggleButton?.setAttribute('aria-expanded', 'true');
      if (this.isMobile()) document.documentElement.classList.add('rf-support-open');
    };

    close = () => {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.panel.classList.remove('is-opening');
      this.classList.remove('is-input-focused');
      this.toggleButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('rf-support-open');
    };

    onKeydown = (event) => {
      if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
        this.close();
        this.toggleButton?.focus();
      }
    };

    onDocumentClick = (event) => {
      if (!this.panel || this.panel.hidden) return;
      // Mobile stays open until the explicit close button is tapped. This avoids
      // theme/drawer click handlers racing with taps on the email/message fields.
      if (this.isMobile()) return;
      if (!this.contains(event.target)) this.close();
    };
  }

  if (!customElements.get('rf-support-widget')) {
    customElements.define('rf-support-widget', RFSupportWidget);
  }
})();

/* =====================================================
   V19 — drawer controls, cart recently viewed, draggable support
===================================================== */
(() => {
  const closeHeaderDropdowns = () => {
    document.querySelectorAll('.header__navigation details[open]').forEach((details) => {
      try {
        if ('open' in details && typeof details.open === 'boolean') details.open = false;
        details.removeAttribute('open');
        details.querySelector(':scope > summary')?.removeAttribute('open');
        details.querySelector(':scope > .dropdown, :scope > .mega-menu')?.removeAttribute('open');
      } catch (_) {
        details.removeAttribute('open');
      }
    });
    document.body.classList.remove('has-dropdown-menu');
  };

  // Make the header controls deterministic: Search always opens SearchDrawer;
  // Cart always opens CartDrawer (including while the shopper is on /cart).
  document.addEventListener(
    'click',
    (event) => {
      const cartControl = event.target.closest?.('.cart-drawer-button');
      const searchControl = event.target.closest?.('.search-drawer-button');
      if (!cartControl && !searchControl) return;

      closeHeaderDropdowns();

      if (cartControl) {
        const drawer = document.getElementById('CartDrawer');
        if (!drawer || typeof drawer.show !== 'function') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const search = document.getElementById('SearchDrawer');
        if (search?.open && typeof search.hide === 'function') search.hide();
        drawer.show(cartControl);
        return;
      }

      if (searchControl) {
        const drawer = document.getElementById('SearchDrawer');
        if (!drawer || typeof drawer.show !== 'function') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const cart = document.getElementById('CartDrawer');
        if (cart?.open && typeof cart.hide === 'function') cart.hide();
        drawer.show(searchControl);
      }
    },
    true
  );

  const rfGetRichViewedV23 = () => {
    try {
      const items = JSON.parse(window.localStorage.getItem('rf:recently-viewed-products') || '[]');
      return Array.isArray(items) ? items.filter((item) => item && item.handle) : [];
    } catch (_) {
      return [];
    }
  };

  const rfFetchProductSectionV23 = async (handle, sectionId) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);
    return response.text();
  };

  if (!customElements.get('rf-cart-recently-viewed')) {
    customElements.define(
      'rf-cart-recently-viewed',
      class RFCartRecentlyViewed extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.load();
        }

        getCartHandles() {
          return new Set((this.dataset.cartProductHandles || '').split(',').map((v) => v.trim()).filter(Boolean));
        }

        async load() {
          const cartHandles = this.getCartHandles();
          const limit = Math.max(1, Number(this.dataset.limit) || 6);
          const rich = rfGetRichViewedV23().filter((item) => !cartHandles.has(item.handle)).slice(0, limit);
          const target = this.querySelector('[data-rf-cart-rv-track]');
          if (!target) return;

          if (!rich.length) {
            // Legacy fallback keeps existing history useful until the shopper visits a product under V23.
            let viewed = [];
            try { viewed = JSON.parse(window.localStorage.getItem('sleektheme:recently-viewed') || '[]'); } catch (_) {}
            const cartIds = new Set((this.dataset.cartProductIds || '').split(',').map((v) => Number(v.trim())).filter(Boolean));
            const ids = viewed.map(Number).filter((id) => id && !cartIds.has(id)).slice(0, limit);
            if (!ids.length || !this.dataset.url) { this.remove(); return; }
            try {
              const query = ids.map((id) => `id:${id}`).join(' OR ');
              const response = await fetch(`${this.dataset.url}${encodeURIComponent(query)}`, { credentials: 'same-origin' });
              const html = await response.text();
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const source = parsed.querySelector('[data-rf-cart-rv-results]');
              const cards = source ? Array.from(source.querySelectorAll('[data-product-id]')) : [];
              if (!cards.length) { this.remove(); return; }
              target.replaceChildren(...cards);
              this.hidden = false;
            } catch (error) {
              console.warn('[RF recently viewed legacy]', error);
              this.remove();
            }
            return;
          }

          try {
            const sectionId = this.dataset.productSectionId || 'rf-cart-recently-viewed';
            const htmlList = await Promise.all(rich.map((item) => rfFetchProductSectionV23(item.handle, sectionId)));
            const cardMap = new Map();
            htmlList.forEach((html) => {
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const source = parsed.querySelector('[data-rf-cart-rv-results]');
              const card = source?.querySelector('[data-product-handle]');
              if (card) cardMap.set(card.dataset.productHandle, card);
            });
            const cards = rich.map((item) => cardMap.get(item.handle)).filter(Boolean);
            if (!cards.length) { this.remove(); return; }
            target.replaceChildren(...cards);
            this.hidden = false;
          } catch (error) {
            console.warn('[RF recently viewed handle loader]', error);
            this.remove();
          }
        }
      }
    );
  }


  const setupSupportDrag = (widget) => {
    if (!widget || widget.dataset.rfDragReady === 'true') return;
    const launcher = widget.querySelector('.rf-support-widget__launcher');
    const panel = widget.querySelector('.rf-support-widget__panel');
    if (!launcher) return;
    widget.dataset.rfDragReady = 'true';

    let state = null;
    let suppressClickUntil = 0;

    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    const key = () => `rf-support-position-${isMobile() ? 'mobile' : 'desktop'}`;
    const target = () => (isMobile() ? launcher : widget);

    const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

    const updatePanelPlacement = () => {
      if (isMobile()) return;
      const rect = launcher.getBoundingClientRect();
      const panelHeight = Math.min(panel?.scrollHeight || 420, Math.max(260, window.innerHeight - 24));
      const panelWidth = Math.min(panel?.scrollWidth || 390, Math.max(280, window.innerWidth - 24));
      const spaceAbove = Math.max(0, rect.top - 12);
      const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - 12);
      const placeBelow = spaceAbove < panelHeight + 12 && spaceBelow > spaceAbove;
      const alignLeft = rect.right < panelWidth + 12;
      widget.classList.toggle('panel-below', placeBelow);
      widget.classList.toggle('panel-align-left', alignLeft);
    };

    const applyPosition = (x, y) => {
      const el = target();
      const rect = launcher.getBoundingClientRect();
      const margin = 8;
      const width = rect.width || launcher.offsetWidth || 56;
      const height = rect.height || launcher.offsetHeight || 56;
      const maxX = window.innerWidth - width - margin;
      const maxY = window.innerHeight - height - margin;
      const left = clamp(x, margin, maxX);
      const top = clamp(y, margin, maxY);

      widget.classList.add('is-user-positioned');
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
      updatePanelPlacement();
      return { left, top, maxX: Math.max(margin, maxX), maxY: Math.max(margin, maxY) };
    };

    const savePosition = () => {
      const rect = launcher.getBoundingClientRect();
      const margin = 8;
      const maxX = Math.max(1, window.innerWidth - rect.width - margin * 2);
      const maxY = Math.max(1, window.innerHeight - rect.height - margin * 2);
      const payload = {
        x: clamp((rect.left - margin) / maxX, 0, 1),
        y: clamp((rect.top - margin) / maxY, 0, 1),
      };
      try {
        window.localStorage.setItem(key(), JSON.stringify(payload));
      } catch (_) {}
    };

    const restorePosition = () => {
      let saved = null;
      try {
        saved = JSON.parse(window.localStorage.getItem(key()) || 'null');
      } catch (_) {}
      if (!saved || typeof saved.x !== 'number' || typeof saved.y !== 'number') {
        widget.classList.remove('is-user-positioned');
        launcher.style.removeProperty('left');
        launcher.style.removeProperty('top');
        if (!isMobile()) {
          widget.style.removeProperty('left');
          widget.style.removeProperty('top');
        }
        return;
      }

      const rect = launcher.getBoundingClientRect();
      const margin = 8;
      const maxX = Math.max(1, window.innerWidth - rect.width - margin * 2);
      const maxY = Math.max(1, window.innerHeight - rect.height - margin * 2);
      applyPosition(margin + saved.x * maxX, margin + saved.y * maxY);
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const rect = launcher.getBoundingClientRect();
      state = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        left: rect.left,
        top: rect.top,
        moved: false,
      };
      launcher.setPointerCapture?.(event.pointerId);
      widget.classList.add('is-dragging');
    };

    const onPointerMove = (event) => {
      if (!state || event.pointerId !== state.pointerId) return;
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;
      if (!state.moved && Math.hypot(dx, dy) < 6) return;
      state.moved = true;
      event.preventDefault();
      applyPosition(state.left + dx, state.top + dy);
    };

    const onPointerEnd = (event) => {
      if (!state || event.pointerId !== state.pointerId) return;
      launcher.releasePointerCapture?.(event.pointerId);
      const moved = state.moved;
      state = null;
      widget.classList.remove('is-dragging');
      if (moved) {
        suppressClickUntil = Date.now() + 450;
        savePosition();
      }
    };

    launcher.addEventListener('pointerdown', onPointerDown);
    launcher.addEventListener('pointermove', onPointerMove, { passive: false });
    launcher.addEventListener('pointerup', onPointerEnd);
    launcher.addEventListener('pointercancel', onPointerEnd);
    launcher.addEventListener(
      'click',
      (event) => {
        if (Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        requestAnimationFrame(updatePanelPlacement);
      },
      true
    );

    const onResize = () => {
      window.clearTimeout(widget._rfDragResizeTimer);
      widget._rfDragResizeTimer = window.setTimeout(restorePosition, 120);
    };
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);

    requestAnimationFrame(restorePosition);
    if (panel && !panel.hidden) requestAnimationFrame(updatePanelPlacement);
  };

  const initSupportDrag = () => setupSupportDrag(document.querySelector('rf-support-widget'));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSupportDrag, { once: true });
  } else {
    initSupportDrag();
  }
})();

/* =====================================================
   V20 — CART CONVERSION LAYER
   Offer milestones, recently-viewed quick add, cart upsell
===================================================== */
(() => {
  const rfParseJsonV20 = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      console.warn('[RF V20] Could not parse JSON payload', error);
      return fallback;
    }
  };

  const rfFormatMoneyV20 = (cents) => {
    const value = Number(cents) || 0;
    try {
      if (window.FoxTheme?.Currency?.formatMoney) {
        return window.FoxTheme.Currency.formatMoney(value, window.FoxTheme.settings.moneyFormat);
      }
    } catch (_) {}
    return `$${(value / 100).toFixed(2)}`;
  };

  // V24: local rich-history reader for this conversion-layer scope.
  // The older helper lives inside a different IIFE and is not visible here.
  const rfGetRichViewedV24 = () => {
    try {
      const items = JSON.parse(window.localStorage.getItem('rf:recently-viewed-products') || '[]');
      return Array.isArray(items) ? items.filter((item) => item && item.handle) : [];
    } catch (_) {
      return [];
    }
  };

  const rfAddCartItemV20 = async (variantId, quantity = 1) => {
    const id = Number(variantId);
    const qty = Math.max(1, Number(quantity) || 1);
    if (!id) throw new Error('Please choose an available variation.');

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    sectionsToBundle = [...new Set(sectionsToBundle.filter(Boolean))];

    const body = JSON.stringify({
      items: [{ id, quantity: qty }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
      ...FoxTheme.utils.fetchConfig('javascript'),
      body,
    });
    const parsedState = await response.json();
    if (!response.ok || parsedState.status) {
      throw new Error(parsedState.description || parsedState.message || 'Could not add this item to cart.');
    }

    const cartResponse = await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig('json', 'GET'),
    });
    const cartJson = await cartResponse.json();
    cartJson.sections = parsedState.sections || {};

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      sources: 'rf-v20-quick-add',
      cart: cartJson,
    });
    document.dispatchEvent(
      new CustomEvent('product-ajax:added', {
        detail: { product: parsedState },
      })
    );
    return cartJson;
  };

  if (!customElements.get('rf-cart-offer-progress')) {
    customElements.define(
      'rf-cart-offer-progress',
      class RFCartOfferProgress extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.fill = this.querySelector('[data-rf-offer-fill]');
          this.message = this.querySelector('[data-rf-offer-message]');
          this.milestoneTwo = this.querySelector('[data-rf-offer-milestone="2"]');
          this.milestoneFour = this.querySelector('[data-rf-offer-milestone="4"]');
          this.milestoneShip = this.querySelector('[data-rf-offer-milestone="ship"]');
          this.dotTwo = this.querySelector('.rf-cart-offer-progress__dot--1');
          this.dotFour = this.querySelector('.rf-cart-offer-progress__dot--2');
          this.dotShip = this.querySelector('.rf-cart-offer-progress__dot--3');
          this.shipGoal = Number(this.dataset.shipGoal || 8500);
          this.onCartUpdated = this.handleCartUpdated.bind(this);
          document.addEventListener('cart:updated', this.onCartUpdated);
          this.update(Number(this.dataset.itemCount || 0), Number(this.dataset.subtotal || 0));
        }

        disconnectedCallback() {
          document.removeEventListener('cart:updated', this.onCartUpdated);
        }

        handleCartUpdated(event) {
          const cart = event.detail?.cart;
          if (!cart) return;
          this.update(Number(cart.item_count || 0), Number(cart.items_subtotal_price || cart.total_price || 0));
        }

        update(count, subtotal) {
          const shipGoal = this.shipGoal || 8500;
          let progress = 0;
          if (subtotal >= shipGoal) {
            progress = 100;
          } else if (count >= 4) {
            progress = 66 + (subtotal / shipGoal) * 34;
          } else if (count >= 2) {
            progress = 33 + (Math.min(2, count - 2) / 2) * 33;
          } else {
            progress = (Math.max(0, count) / 2) * 33;
          }
          progress = Math.max(0, Math.min(100, progress));
          if (this.fill) this.fill.style.width = `${progress}%`;

          const twoDone = count >= 2;
          const fourDone = count >= 4;
          const shipDone = subtotal >= shipGoal;
          [this.milestoneTwo, this.dotTwo].forEach((el) => el?.classList.toggle('is-achieved', twoDone));
          [this.milestoneFour, this.dotFour].forEach((el) => el?.classList.toggle('is-achieved', fourDone));
          [this.milestoneShip, this.dotShip].forEach((el) => el?.classList.toggle('is-achieved', shipDone));

          if (!this.message) return;
          if (count < 2) {
            const left = Math.max(1, 2 - count);
            this.message.innerHTML = `You are ${left} item${left === 1 ? '' : 's'} away from getting <strong>10% off</strong>`;
          } else if (count < 4) {
            const left = Math.max(1, 4 - count);
            this.message.innerHTML = `<strong>10% off unlocked.</strong> ${left} more item${left === 1 ? '' : 's'} to get <strong>15% off</strong>`;
          } else if (!shipDone) {
            this.message.innerHTML = `<strong>15% off unlocked.</strong> Spend ${rfFormatMoneyV20(shipGoal - subtotal)} more to get <strong>free shipping</strong>`;
          } else {
            this.message.innerHTML = '<strong>15% off + free shipping unlocked</strong>';
          }
        }
      }
    );
  }

  if (!customElements.get('rf-cart-recently-viewed-panel')) {
    customElements.define(
      'rf-cart-recently-viewed-panel',
      class RFCartRecentlyViewedPanel extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.toggleButton = this.querySelector('[data-rf-rvq-toggle]');
          this.panel = this.querySelector('[data-rf-rvq-panel]');
          this.closeButton = this.querySelector('[data-rf-rvq-close]');
          this.list = this.querySelector('[data-rf-rvq-list]');
          this.empty = this.querySelector('[data-rf-rvq-empty]');
          this.count = this.querySelector('[data-rf-rvq-count]');
          this.loaded = false;
          this.dirty = true;
          this.toggleButton?.removeAttribute('hidden');
          this.refreshCount();
          this.toggleButton?.addEventListener('click', this.toggle);
          this.closeButton?.addEventListener('click', this.close);
          this.onDocumentClick = this.handleDocumentClick.bind(this);
          this.onKeydown = this.handleKeydown.bind(this);
          this.onCartUpdated = this.handleCartUpdated.bind(this);
          document.addEventListener('click', this.onDocumentClick);
          document.addEventListener('keydown', this.onKeydown);
          document.addEventListener('cart:updated', this.onCartUpdated);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocumentClick);
          document.removeEventListener('keydown', this.onKeydown);
          document.removeEventListener('cart:updated', this.onCartUpdated);
        }

        getCartHandles() {
          return new Set((this.dataset.cartProductHandles || '').split(',').map((value) => value.trim()).filter(Boolean));
        }

        getViewedItems() {
          const limit = Math.max(1, Number(this.dataset.limit) || 20);
          const cartHandles = this.getCartHandles();
          return rfGetRichViewedV24()
            .filter((item) => item?.handle && !cartHandles.has(item.handle))
            .slice(0, limit);
        }

        refreshCount() {
          const items = this.getViewedItems();
          if (this.count) this.count.textContent = String(items.length);
          return items;
        }

        productUrl(handle, suffix = '') {
          const root = window.Shopify?.routes?.root || '/';
          return `${root}products/${encodeURIComponent(handle)}${suffix}`.replace(/\/+/g, '/');
        }

        async fetchProduct(handle) {
          const response = await fetch(this.productUrl(handle, '.js'), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (!response.ok) throw new Error(`Could not load ${handle} (${response.status}).`);
          return response.json();
        }

        normalizeVariantOptions(variant) {
          if (Array.isArray(variant?.options)) return variant.options;
          return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
        }

        normalizeOptionDefinitions(product) {
          const variants = Array.isArray(product?.variants) ? product.variants : [];
          return (Array.isArray(product?.options) ? product.options : []).map((option, index) => {
            const name = typeof option === 'string' ? option : (option?.name || `Option ${index + 1}`);
            const declaredValues = typeof option === 'object' && Array.isArray(option?.values) ? option.values : [];
            const values = declaredValues.length
              ? declaredValues
              : [...new Set(variants.map((variant) => this.normalizeVariantOptions(variant)[index]).filter((value) => value != null))];
            return { name, values };
          });
        }

        createElement(tag, className = '', text = '') {
          const element = document.createElement(tag);
          if (className) element.className = className;
          if (text) element.textContent = text;
          return element;
        }

        buildCard(product, handle) {
          const variants = Array.isArray(product?.variants) ? product.variants : [];
          const selected = variants.find((variant) => variant.available) || variants[0];
          if (!selected) return null;
          variants.forEach((variant) => { variant.options = this.normalizeVariantOptions(variant); });

          const card = this.createElement('article', 'rf-cart-rvq-card');
          card.dataset.rfRvqCard = '';
          card.dataset.productId = String(product.id || '');
          card.dataset.productHandle = handle;

          const productHref = this.productUrl(handle);
          const media = this.createElement('a', 'rf-cart-rvq-card__media blocks-radius-sm');
          media.href = productHref;
          media.setAttribute('aria-label', product.title || 'View product');
          const imageUrl = typeof product.featured_image === 'string'
            ? product.featured_image
            : (product.featured_image?.src || product.images?.[0] || '');
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
            image.alt = product.title || '';
            image.loading = 'lazy';
            media.appendChild(image);
          }

          const body = this.createElement('div', 'rf-cart-rvq-card__body');
          const title = this.createElement('a', 'rf-cart-rvq-card__title', product.title || 'Product');
          title.href = productHref;
          body.appendChild(title);

          const price = this.createElement('div', 'rf-cart-rvq-card__price');
          price.dataset.rfRvqPrice = '';
          const current = this.createElement('strong');
          current.dataset.rfRvqPriceCurrent = '';
          const compare = this.createElement('s');
          compare.dataset.rfRvqPriceCompare = '';
          const save = this.createElement('span', 'rf-cart-rvq-card__save');
          save.dataset.rfRvqPriceSave = '';
          price.append(current, compare, save);
          body.appendChild(price);

          const optionDefinitions = this.normalizeOptionDefinitions(product);
          if (optionDefinitions.length && !(optionDefinitions.length === 1 && optionDefinitions[0].name === 'Title' && optionDefinitions[0].values.length === 1)) {
            const options = this.createElement('div', 'rf-cart-rvq-card__options');
            optionDefinitions.forEach((definition, index) => {
              const field = this.createElement('label', 'rf-cart-rvq-card__field');
              const label = this.createElement('span', '', definition.name);
              const select = document.createElement('select');
              select.dataset.rfRvqOption = '';
              select.dataset.optionIndex = String(index);
              select.setAttribute('aria-label', definition.name);
              definition.values.forEach((value) => {
                const option = document.createElement('option');
                option.value = value;
                option.textContent = value;
                select.appendChild(option);
              });
              field.append(label, select);
              options.appendChild(field);
            });
            body.appendChild(options);
          }

          const actions = this.createElement('div', 'rf-cart-rvq-card__actions');
          const qty = this.createElement('div', 'rf-cart-rvq-card__qty');
          qty.dataset.rfRvqQty = '';
          const minus = this.createElement('button', '', '−');
          minus.type = 'button';
          minus.dataset.rfRvqMinus = '';
          minus.setAttribute('aria-label', 'Decrease quantity');
          const qtyInput = document.createElement('input');
          qtyInput.type = 'number';
          qtyInput.min = '1';
          qtyInput.value = '1';
          qtyInput.inputMode = 'numeric';
          qtyInput.dataset.rfRvqQtyInput = '';
          qtyInput.setAttribute('aria-label', 'Quantity');
          const plus = this.createElement('button', '', '+');
          plus.type = 'button';
          plus.dataset.rfRvqPlus = '';
          plus.setAttribute('aria-label', 'Increase quantity');
          qty.append(minus, qtyInput, plus);

          const add = this.createElement('button', 'rf-cart-rvq-card__add');
          add.type = 'button';
          add.dataset.rfRvqAdd = '';
          const addLabel = this.createElement('span', '', 'Add to cart');
          add.appendChild(addLabel);
          actions.append(qty, add);
          body.appendChild(actions);

          const status = this.createElement('p', 'rf-cart-rvq-card__status');
          status.dataset.rfRvqStatus = '';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          body.appendChild(status);

          const variantsScript = document.createElement('script');
          variantsScript.type = 'application/json';
          variantsScript.dataset.rfRvqVariants = '';
          variantsScript.textContent = JSON.stringify(variants);
          const selectedScript = document.createElement('script');
          selectedScript.type = 'application/json';
          selectedScript.dataset.rfRvqSelectedOptions = '';
          selectedScript.textContent = JSON.stringify(this.normalizeVariantOptions(selected));

          card.append(media, body, variantsScript, selectedScript);
          return card;
        }

        async load() {
          const items = this.refreshCount();
          this.dirty = false;
          if (this.list) this.list.replaceChildren();
          if (!items.length) {
            if (this.empty) {
              this.empty.textContent = 'No recently viewed products yet.';
              this.empty.hidden = false;
            }
            this.loaded = true;
            return;
          }
          if (this.empty) this.empty.hidden = true;

          try {
            const products = await Promise.all(items.map(async (item) => ({ item, product: await this.fetchProduct(item.handle) })));
            const cards = products.map(({ item, product }) => this.buildCard(product, item.handle)).filter(Boolean);
            if (this.list) this.list.replaceChildren(...cards);
            cards.forEach((card) => this.setupCard(card));
            if (this.empty) this.empty.hidden = cards.length > 0;
            if (this.count) this.count.textContent = String(cards.length);
            this.loaded = true;
          } catch (error) {
            console.warn('[RF recently viewed product API]', error);
            if (this.empty) {
              this.empty.textContent = 'Could not load recently viewed products. Open a product page once, then try again.';
              this.empty.hidden = false;
            }
          }
        }

        setupCard(card) {
          const variants = rfParseJsonV20(card.querySelector('[data-rf-rvq-variants]'), []);
          const selectedOptions = rfParseJsonV20(card.querySelector('[data-rf-rvq-selected-options]'), []);
          const selects = Array.from(card.querySelectorAll('[data-rf-rvq-option]'));
          const currentPrice = card.querySelector('[data-rf-rvq-price-current]');
          const comparePrice = card.querySelector('[data-rf-rvq-price-compare]');
          const savePrice = card.querySelector('[data-rf-rvq-price-save]');
          const addButton = card.querySelector('[data-rf-rvq-add]');
          const status = card.querySelector('[data-rf-rvq-status]');
          const qtyInput = card.querySelector('[data-rf-rvq-qty-input]');

          selects.forEach((select, index) => { if (selectedOptions[index] != null) select.value = selectedOptions[index]; });

          const updateVariant = () => {
            const chosen = selects.map((select) => select.value);
            const variant = selects.length
              ? variants.find((candidate) => Array.isArray(candidate.options) && chosen.every((value, index) => candidate.options[index] === value))
              : variants.find((candidate) => candidate.available) || variants[0];
            card._rfVariant = variant || null;
            if (variant) {
              const price = Number(variant.price || 0);
              const compare = Number(variant.compare_at_price || 0);
              if (currentPrice) currentPrice.textContent = rfFormatMoneyV20(price);
              const hasSale = compare > price;
              if (comparePrice) { comparePrice.hidden = !hasSale; comparePrice.textContent = hasSale ? rfFormatMoneyV20(compare) : ''; }
              if (savePrice) { savePrice.hidden = !hasSale; savePrice.textContent = hasSale ? `Save ${rfFormatMoneyV20(compare - price)}` : ''; }
            }
            const unavailable = !variant || variant.available === false;
            if (addButton) {
              addButton.disabled = unavailable;
              const label = addButton.querySelector('span');
              if (label) label.textContent = unavailable ? 'Unavailable' : 'Add to cart';
            }
            if (status) status.textContent = unavailable ? 'This option is unavailable.' : '';
          };

          selects.forEach((select) => select.addEventListener('change', updateVariant));
          card.querySelector('[data-rf-rvq-minus]')?.addEventListener('click', () => { if (qtyInput) qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1) - 1)); });
          card.querySelector('[data-rf-rvq-plus]')?.addEventListener('click', () => { if (qtyInput) qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1) + 1)); });
          qtyInput?.addEventListener('change', () => { qtyInput.value = String(Math.max(1, Number(qtyInput.value || 1))); });

          addButton?.addEventListener('click', async () => {
            const variant = card._rfVariant;
            if (!variant || variant.available === false) return;
            addButton.disabled = true;
            addButton.classList.add('is-loading');
            if (status) status.textContent = 'Adding…';
            try {
              await rfAddCartItemV20(variant.id, Number(qtyInput?.value || 1));
              this.close();
              this.dirty = true;
            } catch (error) {
              if (status) status.textContent = error.message || 'Could not add this item.';
              if (addButton.isConnected) addButton.disabled = false;
            } finally {
              if (addButton.isConnected) addButton.classList.remove('is-loading');
            }
          });
          updateVariant();
        }

        toggle = async () => {
          if (!this.panel || !this.toggleButton) return;
          const willOpen = this.panel.hidden;
          this.panel.hidden = !willOpen;
          this.toggleButton.setAttribute('aria-expanded', String(willOpen));
          this.classList.toggle('is-open', willOpen);
          if (willOpen && (!this.loaded || this.dirty)) await this.load();
        };

        close = () => {
          if (!this.panel || !this.toggleButton) return;
          this.panel.hidden = true;
          this.toggleButton.setAttribute('aria-expanded', 'false');
          this.classList.remove('is-open');
        };

        handleDocumentClick(event) { if (!this.panel || this.panel.hidden || this.contains(event.target)) return; this.close(); }
        handleKeydown(event) { if (event.key === 'Escape') this.close(); }
        handleCartUpdated(event) {
          const items = event.detail?.cart?.items || [];
          this.dataset.cartProductIds = items.map((item) => item.product_id).filter(Boolean).join(',');
          this.dataset.cartProductHandles = items.map((item) => item.handle).filter(Boolean).join(',');
          this.dirty = true;
          this.refreshCount();
          if (!this.panel?.hidden) this.load();
        }
      }
    );
  }


  if (!customElements.get('rf-cart-upsell')) {
    customElements.define(
      'rf-cart-upsell',
      class RFCartUpsell extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.track = this.querySelector('[data-rf-upsell-track]');
          this.prevButton = this.querySelector('[data-rf-upsell-prev]');
          this.nextButton = this.querySelector('[data-rf-upsell-next]');
          this.querySelectorAll('[data-rf-upsell-card]').forEach((card) => this.setupCard(card));

          this.onPrevClick = () => this.scrollTrack(-1);
          this.onNextClick = () => this.scrollTrack(1);
          this.onTrackScroll = () => this.updateNavState();
          this.onTrackKeydown = (event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.scrollTrack(-1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.scrollTrack(1);
            }
          };
          this.onPointerDown = (event) => this.startDesktopDrag(event);
          this.onPointerMove = (event) => this.moveDesktopDrag(event);
          this.onPointerUp = (event) => this.endDesktopDrag(event);
          this.onTrackClickCapture = (event) => {
            if (!this.suppressNextClick) return;
            event.preventDefault();
            event.stopPropagation();
            this.suppressNextClick = false;
          };

          this.prevButton?.addEventListener('click', this.onPrevClick);
          this.nextButton?.addEventListener('click', this.onNextClick);
          this.track?.addEventListener('scroll', this.onTrackScroll, { passive: true });
          this.track?.addEventListener('keydown', this.onTrackKeydown);
          this.track?.addEventListener('pointerdown', this.onPointerDown);
          this.track?.addEventListener('pointermove', this.onPointerMove);
          this.track?.addEventListener('pointerup', this.onPointerUp);
          this.track?.addEventListener('pointercancel', this.onPointerUp);
          this.track?.addEventListener('click', this.onTrackClickCapture, true);
          requestAnimationFrame(() => this.updateNavState());
        }

        disconnectedCallback() {
          this.prevButton?.removeEventListener('click', this.onPrevClick);
          this.nextButton?.removeEventListener('click', this.onNextClick);
          this.track?.removeEventListener('scroll', this.onTrackScroll);
          this.track?.removeEventListener('keydown', this.onTrackKeydown);
          this.track?.removeEventListener('pointerdown', this.onPointerDown);
          this.track?.removeEventListener('pointermove', this.onPointerMove);
          this.track?.removeEventListener('pointerup', this.onPointerUp);
          this.track?.removeEventListener('pointercancel', this.onPointerUp);
          this.track?.removeEventListener('click', this.onTrackClickCapture, true);
        }

        scrollTrack(direction) {
          if (!this.track) return;
          const firstCard = this.track.querySelector('[data-rf-upsell-card]');
          const styles = window.getComputedStyle(this.track);
          const gap = parseFloat(styles.columnGap || styles.gap || '14') || 14;
          const amount = (firstCard?.getBoundingClientRect().width || this.track.clientWidth * 0.78) + gap;
          this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
        }

        updateNavState() {
          if (!this.track) return;
          const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
          if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
        }

        startDesktopDrag(event) {
          if (!this.track || event.pointerType !== 'mouse' || event.button !== 0) return;
          if (event.target.closest('button, select, input, textarea, label')) return;
          this.dragging = true;
          this.dragMoved = false;
          this.suppressNextClick = false;
          this.dragPointerId = event.pointerId;
          this.dragStartX = event.clientX;
          this.dragStartScrollLeft = this.track.scrollLeft;
          this.track.setPointerCapture?.(event.pointerId);
        }

        moveDesktopDrag(event) {
          if (!this.dragging || event.pointerId !== this.dragPointerId || !this.track) return;
          const delta = event.clientX - this.dragStartX;
          if (Math.abs(delta) > 4) {
            this.dragMoved = true;
            this.track.classList.add('is-dragging');
            this.track.scrollLeft = this.dragStartScrollLeft - delta;
            event.preventDefault();
          }
        }

        endDesktopDrag(event) {
          if (!this.dragging || event.pointerId !== this.dragPointerId) return;
          this.dragging = false;
          this.suppressNextClick = Boolean(this.dragMoved);
          this.track?.classList.remove('is-dragging');
          try { this.track?.releasePointerCapture?.(event.pointerId); } catch (_) {}
          this.updateNavState();
          if (this.suppressNextClick) {
            window.setTimeout(() => { this.suppressNextClick = false; }, 0);
          }
        }

        setupCard(card) {
          const variants = rfParseJsonV20(card.querySelector('[data-rf-upsell-variants]'), []);
          const selectedOptions = rfParseJsonV20(card.querySelector('[data-rf-upsell-selected-options]'), []);
          const selects = Array.from(card.querySelectorAll('[data-rf-upsell-option]'));
          const addButton = card.querySelector('[data-rf-upsell-add]');
          const currentPrice = card.querySelector('[data-rf-upsell-price-current]');
          const comparePrice = card.querySelector('[data-rf-upsell-price-compare]');
          const savePrice = card.querySelector('[data-rf-upsell-price-save]');
          const status = card.querySelector('[data-rf-upsell-status]');

          selects.forEach((select, index) => { if (selectedOptions[index] != null) select.value = selectedOptions[index]; });

          const syncVariant = () => {
            const chosen = selects.map((select) => select.value);
            const variant = selects.length
              ? variants.find((candidate) => Array.isArray(candidate.options) && chosen.every((value, index) => candidate.options[index] === value))
              : variants.find((candidate) => candidate.available) || variants[0];
            card._rfVariant = variant || null;
            const unavailable = !variant || variant.available === false;
            if (variant) {
              addButton.dataset.variantId = variant.id;
              const price = Number(variant.price || 0);
              const compare = Number(variant.compare_at_price || 0);
              const hasSale = compare > price;
              if (currentPrice) currentPrice.innerHTML = rfFormatMoneyV20(price);
              if (comparePrice) { comparePrice.hidden = !hasSale; comparePrice.innerHTML = hasSale ? rfFormatMoneyV20(compare) : ''; }
              if (savePrice) { savePrice.hidden = !hasSale; savePrice.innerHTML = hasSale ? `Save ${rfFormatMoneyV20(compare - price)}` : ''; }
            }
            if (addButton) {
              addButton.disabled = unavailable;
              const label = addButton.querySelector('span');
              if (label) label.textContent = unavailable ? 'Unavailable' : 'Add';
            }
            if (status) status.textContent = unavailable ? 'This option is unavailable.' : '';
          };

          selects.forEach((select) => select.addEventListener('change', syncVariant));
          syncVariant();

          addButton?.addEventListener('click', async () => {
            const variant = card._rfVariant;
            const variantId = Number(variant?.id || addButton.dataset.variantId || 0);
            if (!variantId || variant?.available === false) return;
            addButton.disabled = true;
            addButton.classList.add('is-loading');
            if (status) status.textContent = 'Adding…';
            try {
              await rfAddCartItemV20(variantId, 1);
            } catch (error) {
              if (status) status.textContent = error.message || 'Could not add this item.';
              if (addButton.isConnected) addButton.disabled = false;
            } finally {
              if (addButton.isConnected) addButton.classList.remove('is-loading');
            }
          });
        }
      }
    );
  }

  // Preserve native focus/keyboard for discount fields in both cart views.
  // The theme's bubbling touchend handler cancels text-input default behavior.
  // Capture only these fields, without preventDefault or synthetic clicks.
  document.addEventListener('touchend', (event) => {
    const input = event.target.closest?.(
      '.rf-cart-discount-form input[name="discount"], form[is="cart-discount"] input[name="discount"]'
    );
    if (!input || input.disabled || input.readOnly) return;
    event.stopPropagation();
  }, { capture: true, passive: true });

  // Delegation also covers cart drawer HTML replaced after a cart update.
  // Keep <details> open until its closing animation finishes.
  const discountAnimations = new WeakMap();
  document.addEventListener('click', (event) => {
    const summary = event.target.closest?.('.rf-cart-discount-section > summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (!details || event.defaultPrevented) return;
    event.preventDefault();
    const previous = discountAnimations.get(details);
    const opening = previous ? !previous.opening : !details.open;
    const start = details.getBoundingClientRect().height;
    if (previous) {
      previous.animation.onfinish = null;
      previous.animation.cancel();
    }
    details.open = true;
    const end = opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
    summary.setAttribute('aria-expanded', String(opening));
    details.classList.toggle('is-closing', !opening);
    if (!details.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = opening;
      details.classList.remove('is-closing');
      discountAnimations.delete(details);
      return;
    }
    const animation = details.animate([
      { height: `${start}px`, overflow: 'hidden' },
      { height: `${end}px`, overflow: 'hidden' },
    ], { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    discountAnimations.set(details, { animation, opening });
    animation.onfinish = () => {
      details.open = opening;
      details.classList.remove('is-closing');
      discountAnimations.delete(details);
    };
  });

})();
